import { useCallback, useEffect, useRef, useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import type { Phrase } from "./PhraseTextField";
import { getPhraseFirstLinePreview } from "./PhraseTextField.utils";

type PhraseBodyTextCellProps = {
  phrase: Phrase;
  expanded: boolean;
  onToggleExpansion: (phraseId: string) => void;
};

const PhraseBodyTextCell = ({ phrase, expanded, onToggleExpansion }: PhraseBodyTextCellProps) => {
  const { t } = useTranslation(["common"]);

  const textRef = useRef<HTMLParagraphElement>(null);
  const previewMeasureRef = useRef<HTMLSpanElement>(null);

  const [previewOverflows, setPreviewOverflows] = useState(false);

  const preview = getPhraseFirstLinePreview(phrase.bodyText);
  const showExpansionToggle = /[\r\n]/u.test(phrase.bodyText) || previewOverflows;

  const handleBodyTextExpansionToggle = useCallback(
    () => onToggleExpansion(phrase.id),
    [onToggleExpansion, phrase.id],
  );

  useEffect(() => {
    const previewElement = previewMeasureRef.current;
    const textElement = textRef.current;
    if (!previewElement || !textElement) return;

    const checkOverflow = () => setPreviewOverflows(previewElement.scrollWidth > textElement.clientWidth);
    checkOverflow();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(textElement);
    return () => observer.disconnect();
  }, [preview]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        py: 1,
      }}
    >
      {/* hidden full length preview that is used to determine whether expand arrow is needed */}
      <Typography
        component="span"
        ref={previewMeasureRef}
        variant="body2"
        aria-hidden
        sx={{ position: "absolute", visibility: "hidden", whiteSpace: "nowrap", width: "max-content" }}
      >
        {preview}
      </Typography>
      <Typography
        ref={textRef}
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          overflowWrap: "anywhere",
          textOverflow: expanded ? undefined : "ellipsis",
          whiteSpace: expanded ? "pre-wrap" : "nowrap",
          pt: 0.5,
        }}
      >
        {expanded ? phrase.bodyText : preview}
      </Typography>
      {/* Reserve the expansion arrow's space so overflow detection cannot change the measured layout. */}
      <IconButton
        size="small"
        sx={{
          flexShrink: 0,
          ml: 0.5,
          width: 30,
          height: 30,
          visibility: showExpansionToggle ? "visible" : "hidden",
        }}
        aria-label={`${t(expanded ? "common:collapse" : "common:expand")} ${phrase.name}`}
        aria-expanded={expanded}
        onClick={handleBodyTextExpansionToggle}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
};

export default PhraseBodyTextCell;
